import {
  csosApi,
  csosApproverApi,
  csosUpmApi,
  ErrorCode,
  ICancelRequestOutput,
  IError,
  includes,
  IRequest,
  IRequestDetails,
  IRequestEnrollmentVerificationInput,
  ISaveRequestInput,
  ISaveRequestOutput,
  parseDate,
  ResponseCSOSHeaders,
} from '..';
import consentjson from '../../consent.json';
import {
  compose as lcompose,
  filter as lfiter,
  map as lmap,
  omit as lomit,
} from 'lodash/fp';
import { pathOr, pick } from 'rambda';
import { left, right, Either } from 'fp-ts/Either';
import { chain } from 'fp-ts/Reader';
import { pipe, flow } from 'fp-ts/function';

export const requestEnrollmentVerification =
  (hR: ResponseCSOSHeaders) => (input: IRequestEnrollmentVerificationInput) =>
    csosApi(`${process.env.REACT_APP_API_REQUEST_MGMT_URL}`)(hR)
      .post(`${process.env.REACT_APP_REQUEST_ENROLLMENT_VERIFICATION}`, input)
      .then(r => right(r.data as ISaveRequestOutput))
      .catch(
        lcompose(
          left,
          (message: string) => ({ message, code: ErrorCode.SaveRequestError }),
          pathOr(
            'request-enrollment-verification error',
            'response.data.message'
          )
        )
      );

const getRequestApiConfig = (sailpoint: boolean) => (requestStatus: string) =>
  lcompose(pathOr(null, `${requestStatus}`), getRequestApiConfigs)(sailpoint);

const getRequestApiConfigs = (sailpoint: boolean) =>
  sailpoint
    ? {
        NEW: {
          request: process.env.REACT_APP_API_SAVE_REQUEST,
          document: '',
          baseURL: `${process.env.REACT_APP_API_USER_PROFILE_MGMT_URL}`,
        },
        CANCEL: {
          request: process.env.REACT_APP_API_SAVE_REQUEST,
          document: '',
          baseURL: `${process.env.REACT_APP_API_USER_PROFILE_MGMT_URL}`,
        },
        SUBMITTED: {
          request: process.env.REACT_APP_API_SUBMIT_REQUEST,
          document: process.env.REACT_APP_API_SAVE_DOCUMENT,
          baseURL: `${process.env.REACT_APP_API_REQUEST_MGMT_URL}`,
        },
      }
    : {
        NEW: {
          request: process.env.REACT_APP_API_SAVE_REQUEST,
          document: process.env.REACT_APP_API_SAVE_DOCUMENT,
          baseURL: `${process.env.REACT_APP_API_USER_PROFILE_MGMT_URL}`,
        },
        SUBMITTED: {
          request: process.env.REACT_APP_API_SAVE_REQUEST,
          document: process.env.REACT_APP_API_SAVE_DOCUMENT,
          baseURL: `${process.env.REACT_APP_API_USER_PROFILE_MGMT_URL}`,
        },
      };

export const saveRequest =
  (sailpoint: boolean) =>
  (hR: ResponseCSOSHeaders) =>
  (request: ISaveRequestInput): Promise<Either<IError, ISaveRequestOutput>> => {
    return pipe(
      void 0,
      requestConfig,
      chain(requestApi)
    )({ hR, sailpoint, request });
  };

const requestConfig: () => ({
  hR,
  sailpoint,
  request,
}: {
  hR: ResponseCSOSHeaders;
  request: ISaveRequestInput;
  sailpoint: boolean;
}) => {
  request: string;
  baseURL: string;
} | null =
  () =>
  ({
    hR,
    sailpoint,
    request,
  }: {
    hR: ResponseCSOSHeaders;
    request: ISaveRequestInput;
    sailpoint: boolean;
  }) =>
    lcompose(getRequestApiConfig(sailpoint), pathOr('NEW', 'status'))(request);

const requestApi =
  (a: { request: string; baseURL: string } | null) =>
  ({
    hR,
    sailpoint,
    request,
  }: {
    hR: ResponseCSOSHeaders;
    request: ISaveRequestInput;
    sailpoint: boolean;
  }) =>
    a === null
      ? Promise.resolve(
          left({
            code: ErrorCode.SaveRequestError,
            message: 'error',
          } as IError)
        )
      : csosApi(a.baseURL)(hR)
          .post(
            a.request.replace('requstId', String(request.requestId)),
            saveRequestInput(sailpoint)(request)
          )
          .then(r => right(r.data as ISaveRequestOutput))
          .catch(e =>
            left({
              code: ErrorCode.SaveRequestError,
              message: pathOr(
                'Failed to save request',
                'response.data.error'
              )(e),
            } as IError)
          );

const saveRequestInput = (sailpoint: boolean) => (request: ISaveRequestInput) =>
  sailpoint && request.status === 'SUBMITTED' && request.reqType === 'ENROLL'
    ? pick(
        includes(['POA', 'APO', 'POP', 'APC', 'AAC'])(request.role)
          ? ['statement', 'agreeToTerms', 'poaStatement', 'poaAgreeToTerms']
          : ['statement', 'agreeToTerms']
      )({
        statement: consentByRequestRole(request.role),
        agreeToTerms: true,
        poaStatement: consentjson.poaLetterOnFileConsent,
        poaAgreeToTerms: true,
      })
    : request.status === 'CANCEL'
    ? {
        id: request.requestId,
        deaInfos: [],
        reqType: 'ENROLL',
        role: request.role || 'REG',
        status: 'NEW',
        statement: null,
        agreeToTerms: true,
        comment: null,
      }
    : lomit(['secondaryRole', 'thirdRole'])({
        ...request,
        id: request.requestId,
        statement: statementByRequestStatusRole(request.status)(request.role),
        agreeToTerms: request.status === 'SUBMITTED' ? true : void 0,
        deaInfos:
          request.reqType === 'REVOKE' || request.reqType === 'RENEWAL'
            ? lfiter({ selected: true })(request.deaInfos)
            : request.deaInfos,
        role:
          request.thirdRole ||
          request.secondaryRole ||
          request.role ||
          request.deaInfos[0].role,
        comment:
          request.comments === 'Other'
            ? request.otherComments || request.comments
            : request.comments,
      });

const statementByRequestStatusRole = (status: string) => (role: string) =>
  status === 'SUBMITTED' ? consentByRequestRole(role) : void 0;

const consentByRequestRole = (role: string) =>
  role === 'POA' ? consentjson.poaConsent : consentjson.consent;

export const saveDocument =
  (sailpoint: boolean) =>
  (hR: ResponseCSOSHeaders) =>
  (request: ISaveRequestInput) =>
  (file: File) => {
    var formData = new FormData();
    formData.append('files', file);
    formData.append('categories', 'POA_LETTER');

    return lcompose(
      (endpoint: string) =>
        csosUpmApi(hR)
          .post(endpoint, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          .then(() => right(true))
          .catch(() => left(false)),
      (endpoint: string) =>
        endpoint.replace('requstId', String(request.requestId)),
      pathOr('', 'document'),
      getRequestApiConfig(sailpoint),
      pathOr('NEW', 'status')
    )(request);
  };

export const getRequests = (hR: ResponseCSOSHeaders) => {
  return csosUpmApi(hR)
    .get(`v1/api/upm/request?page=0&limit=10000`)
    .then(
      lcompose(
        right,
        lmap<any, IRequest>(a => ({
          ...a,
          updatedAt: parseDate(a.updatedAt),
        })),
        pathOr([], 'data.content')
      )
    )
    .catch(
      lcompose(
        left,
        (message: string) => ({ message, code: ErrorCode.GetRequestsError }),
        pathOr('upm/request?page=0&limit=10000 error', 'response.data.message')
      )
    );
};

export const getRequestByRequestId =
  (hR: ResponseCSOSHeaders) => (requestId: number) =>
    csosUpmApi(hR)
      .get(`v1/api/upm/request/${requestId}`)
      .then(r => right(r.data as IRequestDetails))
      .catch(e => left(e as IError));

export const getRequest = (hR: ResponseCSOSHeaders) =>
  csosUpmApi(hR)
    .get(`v1/api/upm/request/previous`)
    .then(r => right(r.data as IRequestDetails))
    .catch(e => left(e as IError));

export const getApproverRequests = (hR: ResponseCSOSHeaders) => {
  return csosApproverApi(hR)
    .get(`v1/api/approver/request?page=0&limit=10000`)
    .then(
      lcompose(
        right,
        lmap<any, IRequest>(a => ({
          ...a,
          updatedAt: parseDate(a.updatedAt),
        })),
        pathOr([], 'data.content')
      )
    )
    .catch(e => left(e as IError));
};

export const getApproverRequestByRequestId =
  (hR: ResponseCSOSHeaders) => (requestId: number) =>
    csosApproverApi(hR)
      .get(`v1/api/approver/request/${requestId}`)
      .then(r => right(r.data as IRequestDetails))
      .catch(e => left(e as IError));

// export const getApproverRequests = (token: string) =>
//   Promise.resolve(
//     right([
//       {
//         requestId: 380,
//         reqType: 'ENROLL',
//         userProfileReqId: null,
//         userProfileBenId: null,
//         role: 'REG',
//         status: 'IN_PROCESS',
//         approverComments: null,
//       },
//     ] as IRequest[])
//   );

//

export const cancelRequests =
  (hR: ResponseCSOSHeaders) => (requestIds: number[]) =>
    csosApi(`${process.env.REACT_APP_API_REQUEST_MGMT_URL}`)(hR)
      .put(`${process.env.REACT_APP_API_REQUEST_CANCEL}`, requestIds)
      .then(flow(pathOr<ICancelRequestOutput[]>([])('data'), right))
      .catch(
        lcompose(
          left,
          (message: string) => ({
            message,
            code: ErrorCode.CancelRequestError,
          }),
          pathOr('cancel request error', 'response.data')
        )
      );
